import { makeAutoObservable } from "mobx";
import { IdMap, RArray } from "../../collections";
import { Price } from "../Price";
import { Availability } from "../Availability";
import { Quantity } from "../Quantity";
/*
 * Collection of OrderLines added by user
 * It's price is a nominal order price (without delivery and promotions)
 * It's availability depends on availability of all ordered product instances
 */
export class PurchaseOrderPayload {
    constructor(menu) {
        this._orderLines = [];
        this.menu = menu;
        makeAutoObservable(this);
    }
    static empty(menu) {
        return new PurchaseOrderPayload(menu);
    }
    get orderLines() {
        return new RArray(this._orderLines).filtered((orderLine) => !orderLine.isEmpty);
    }
    get numberOfItems() {
        return this.orderLines.reduce((acc, { quantity }) => acc.add(quantity), Quantity.Zero);
    }
    get price() {
        return Price.total(this.orderLines.map((orderline) => orderline.totalPrice));
    }
    get availability() {
        if (this.orderLines.isEmpty) {
            return Availability.unavailable("EmptyOrder");
        }
        const orderLinesAvailabilities = this.orderLines.map((orderLine) => orderLine.availability).raw;
        return Availability.dependent("OrderLine", "", Availability.composite(orderLinesAvailabilities));
    }
    orderLineIndex(productInstanceId) {
        return this.orderLines.firstIndexWhere((otherLine) => otherLine.id.eq(productInstanceId));
    }
    addOrderLine(orderLine) {
        if (this.orderLineIndex(orderLine.id) !== null) {
            throw new Error(`OrderLine ${orderLine.id.value} is already a part of PurchaseOrder`);
        }
        if (orderLine.isEmpty) {
            throw new Error(`Unable to add an empty OrderLine ${orderLine.id.value} to PurchaseOrder`);
        }
        if (!this.menu.includes(orderLine.productTypeIds)) {
            throw new Error(`Unable to add an OrderLine ${orderLine.id.value}, because some of the product types are offered by restaurant`);
        }
        this._orderLines.push(orderLine);
    }
    removeOrderLine(productInstanceId) {
        const deletedIndex = this.orderLineIndex(productInstanceId);
        if (deletedIndex === null) {
            throw new Error(`OrderLine ${productInstanceId} is not a part of PurchaseOrder`);
        }
        this._orderLines.splice(deletedIndex, 1);
    }
    clear() {
        this._orderLines = [];
    }
    get packingItems() {
        return this.orderLines
            .map((orderLine) => orderLine.packingItems)
            .reduce((prev, curr) => prev.unionWith(curr, (a, b) => a.merge(b)), IdMap.empty());
    }
    get checkoutData() {
        return this.orderLines.map((orderLine) => orderLine.checkoutData).raw;
    }
}
